<template>
  <v-app>
    <v-navigation-drawer
      v-if="authStore.isAuthenticated && !isMobileRoute"
      v-model="drawer"
      app
    >
      <div class="d-flex align-center justify-center pa-4">
        <img 
          :src="isDark ? logoDark : logoDay" 
          alt="VR Logo" 
          style="max-height: 48px; width: auto;"
        />
      </div>
      
      <v-divider />
      
      <v-list-item
        :title="authStore.user?.display_name"
        :subtitle="authStore.user?.email"
        prepend-icon="mdi-account-circle"
      />
      
      <v-divider />

      <v-list density="compact" nav>
        <v-list-item
          prepend-icon="mdi-view-dashboard"
          title="Dashboard"
          :to="{ name: 'dashboard' }"
        />
        
        <v-list-item
          v-if="authStore.isManager"
          prepend-icon="mdi-monitor-dashboard"
          title="Manager Dashboard"
          :to="{ name: 'manager-dashboard' }"
        />
        
        <v-list-item
          prepend-icon="mdi-clock-outline"
          title="Meine Stundenzettel"
          :to="{ name: 'timesheets' }"
        />

        <template v-if="authStore.isProjectLead || authStore.isManager">
          <v-divider class="my-2" />
          <v-list-subheader>Projektleitung</v-list-subheader>
          
          <v-list-item
            prepend-icon="mdi-clipboard-check-outline"
            title="Stundenzettel-Freigaben"
            :to="{ name: 'approvals' }"
          />
          
          <v-list-item
            prepend-icon="mdi-chart-timeline-variant"
            title="Projekt-Stundenübersicht"
            :to="{ name: 'project-hours' }"
          />
        </template>

        <template v-if="authStore.isManager">
          <v-divider class="my-2" />
          <v-list-subheader>Auswertungen</v-list-subheader>
          
          <v-list-item
            prepend-icon="mdi-file-pdf-box"
            title="Reports pro Monat"
            :to="{ name: 'reports-monthly' }"
          />
          
          <v-list-item
            prepend-icon="mdi-chart-bar"
            title="Projekt-Jahresübersicht"
            :to="{ name: 'reports-projects' }"
          />
        </template>

        <template v-if="authStore.isAdmin">
          <v-divider class="my-2" />
          <v-list-subheader>Administration</v-list-subheader>
          
          <v-list-item
            prepend-icon="mdi-account-group"
            title="Mitarbeiter"
            :to="{ name: 'admin-employees' }"
          />
          
          <v-list-item
            prepend-icon="mdi-scale-balance"
            title="Überstunden & Urlaub"
            :to="{ name: 'admin-balances' }"
          />
          
          <v-list-item
            prepend-icon="mdi-briefcase-outline"
            title="Projekte"
            :to="{ name: 'admin-projects' }"
          />
          
          <v-list-item
            prepend-icon="mdi-calendar-star"
            title="Feiertage"
            :to="{ name: 'admin-holidays' }"
          />
          
          <v-list-item
            prepend-icon="mdi-history"
            title="Audit-Log"
            :to="{ name: 'admin-audit-log' }"
          />
          
          <v-list-item
            prepend-icon="mdi-cog"
            title="Einstellungen"
            :to="{ name: 'admin-settings' }"
          />
          
          <v-list-item
            prepend-icon="mdi-email-alert-outline"
            title="Erinnerung senden"
            @click="showReminderDialog = true"
          />
        </template>
      </v-list>

      <template v-if="buildTime && (authStore.isAdmin || authStore.isManager)">
        <v-spacer />
        <div class="text-caption text-grey pa-3" style="opacity: 0.6;">
          Build: {{ buildTime }}
        </div>
      </template>
    </v-navigation-drawer>

    <v-app-bar v-if="authStore.isAuthenticated && !isMobileRoute" app :color="isDark ? 'surface' : 'primary'">
      <v-app-bar-nav-icon @click="drawer = !drawer" />
      
      <img 
        :src="logoDark" 
        alt="VR Logo" 
        class="ml-2 mr-3"
        style="height: 36px; width: auto;"
      />
      
      <v-toolbar-title>
        <span class="font-weight-bold">HERMES</span>
        <span class="text-caption ml-2">Zeiterfassung</span>
      </v-toolbar-title>

      <v-spacer />

      <v-chip
        v-if="sessionRemaining"
        size="small"
        variant="text"
        :color="sessionExpiringSoon ? 'warning' : ''"
        class="mr-1 text-caption"
      >
        <v-icon start size="small">mdi-clock-outline</v-icon>
        {{ sessionRemaining }}
      </v-chip>

      <v-btn icon @click="toggleTheme">
        <v-icon>{{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      </v-btn>

      <v-menu>
        <template #activator="{ props }">
          <v-btn icon v-bind="props">
            <v-icon>mdi-account</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item :to="{ name: 'profile' }">
            <v-list-item-title>Profil</v-list-item-title>
          </v-list-item>
          <v-list-item @click="logout">
            <v-list-item-title>Abmelden</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <v-container fluid>
        <router-view />
      </v-container>
    </v-main>

    <v-snackbar
      v-model="notificationStore.show"
      :color="notificationStore.color"
      :timeout="notificationStore.timeout"
    >
      {{ notificationStore.message }}
      <template #actions>
        <v-btn variant="text" @click="notificationStore.hide()">
          Schließen
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Session expiration warning -->
    <v-snackbar
      v-model="authStore.sessionExpiringSoon"
      color="warning"
      :timeout="-1"
      location="top"
    >
      <v-icon start>mdi-clock-alert-outline</v-icon>
      Ihre Sitzung läuft in Kürze ab. Bitte speichern Sie Ihre Eingaben und melden Sie sich erneut an.
      <template #actions>
        <v-btn variant="text" @click="authStore.sessionExpiringSoon = false">
          OK
        </v-btn>
        <v-btn variant="text" @click="logout">
          Jetzt abmelden
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Timesheet Reminder Dialog (global) -->
    <TimesheetReminderDialog v-if="authStore.isAdmin" v-model="showReminderDialog" />
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useTheme } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import TimesheetReminderDialog from '@/components/TimesheetReminderDialog.vue'

const drawer = ref(true)
const theme = useTheme()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()
const showReminderDialog = ref(false)

// Build timestamp for version verification (set by Vite at build/HMR time)
declare const __BUILD_TIME__: string
const buildTime = computed(() => {
  try {
    const d = new Date(__BUILD_TIME__)
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return '' }
})

const isMobileRoute = computed(() => route.meta.mobileLayout === true)

const isDark = computed(() => theme.global.current.value.dark)

// Session countdown
const now = ref(Date.now())
let clockInterval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  clockInterval = setInterval(() => { now.value = Date.now() }, 30_000) // update every 30s
})
onUnmounted(() => {
  if (clockInterval) clearInterval(clockInterval)
})

const sessionExpiringSoon = computed(() => authStore.sessionExpiringSoon)

const sessionRemaining = computed(() => {
  if (!authStore.sessionExpiresAt) return null
  const diff = authStore.sessionExpiresAt.getTime() - now.value
  if (diff <= 0) return '0:00'
  const hours = Math.floor(diff / 3_600_000)
  const minutes = Math.floor((diff % 3_600_000) / 60_000)
  return `${hours}:${String(minutes).padStart(2, '0')}`
})

// Logo variants for different backgrounds
const logoDay = '/vr_logo_day.png'
const logoDark = '/vr_logo_dark.png'

function toggleTheme() {
  theme.global.name.value = isDark.value ? 'light' : 'dark'
}

async function logout() {
  await authStore.logout()
  router.push({ name: 'login' })
}
</script>
